import {
  MailingAddress,
  School,
} from '@/pages/providers/components/SchoolForm/schemas/different-mailing-address.schema.ts'
import { StandardizedTestsSchema } from '@/pages/providers/components/SchoolForm/schemas/standardized-tests.schema.ts'
import { TuitionSchema } from '@/pages/providers/components/SchoolForm/schemas/tuition.shema.ts'
import { SchoolFormValues } from '@/pages/providers/components/SchoolForm/schoolForm.schema.ts'
import { ProviderDetail } from '@/schemas/provider.schema'

const boolToYesNo = (value: boolean | null | undefined): 'Yes' | 'No' =>
  value ? 'Yes' : 'No'

function normalizeMailingAddress(
  data: School,
  providerInfo: ProviderDetail
): MailingAddress {
  return {
    isMailingAddressSame: boolToYesNo(data.is_mailing_address_same === 'Yes'),
    mailingStreetAddress: providerInfo.mailing_address ?? '',
    mailingStreetAddress2: providerInfo.mailing_address_line_2 ?? '',
    mailingCity: providerInfo.mailing_city ?? '',
    mailingState: providerInfo.mailing_global_state_id?.toString() ?? undefined,
    mailingZipCode: providerInfo.mailing_zip_code ?? '',
  }
}

export const mapSchoolFormValues = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schoolApplication: any,
  providerInfo: ProviderDetail
): SchoolFormValues => ({
  schoolName: schoolApplication.school_name || '',
  streetAddress: providerInfo?.address || '',
  streetAddressLine2: providerInfo?.address_line_2 || '',
  city: providerInfo?.city || '',
  state: providerInfo?.global_state_id?.toString() || '',
  postalZipCode: providerInfo.zipcode || '',
  county: schoolApplication.county || '',
  area: schoolApplication.school_area_location || '',
  lowestGrade: schoolApplication.lowest_grade_served || '',
  highestGrade: schoolApplication.highest_grade_served || '',
  religiousAffiliation: schoolApplication.religious_affiliation || '',
  coedStatus: schoolApplication.coed_status || '',
  hybridStatus: schoolApplication.hybrid_status || '',
  areasOfSpecialization:
    schoolApplication.areas_of_specialization?.split(', ') || [],
  learningDifferences:
    schoolApplication.learning_differences_accommodated?.split(', ') || [],
  additionalPrograms:
    schoolApplication.additional_programs_offered?.split(', ') || [],
  accreditationBodies:
    schoolApplication.accreditation_bodies?.split(', ') || [],
  perPupilExpenditure: schoolApplication.per_pupil_expenditures || '',
  factsContract: schoolApplication.contracted_with_facts || '',
  maxSchoolCapacity: schoolApplication.maximum_school_capacity || '',
  currentEnrollment: schoolApplication.current_enrollment || '',
  percentLowIncome: schoolApplication.percentage_350_poverty_guidelines || '',
  additionalLowIncome: schoolApplication.additional_students_350_poverty || '',
  ratioFullTime: schoolApplication.student_teacher_ratio_full_time || '',
  ratioFullTimePartTime: schoolApplication.student_teacher_ratio_all || '',

  primaryContactFirstName: providerInfo.fpc_first_name || '',
  primaryContactLastName: providerInfo.fpc_last_name || '',
  primaryContactTitle: schoolApplication.primary_contact_title || '',
  primaryContactPhone: providerInfo.phone || '',
  primaryContactEmail: providerInfo.fpc_email || '',
  primaryContactRole: schoolApplication.primary_contact_role || '',
  percentStudentsESL: schoolApplication.percentage_esl_students || '',
  percentParentsESL: schoolApplication.percentage_esl_parents || '',
  isNewSchool: schoolApplication.new_school_to_ace || '',
  yearEstablished: schoolApplication.year_established || '',
  schoolHistory: schoolApplication.history_of_school || '',
  schoolWebsite: schoolApplication.school_website || '',
  onlinePresence: schoolApplication.online_information_source || '',
  missionStatement: schoolApplication.mission_statement || '',
  schoolLogo: schoolApplication.school_picture || null,
  attestation:
    schoolApplication.attestation === '1' ||
    schoolApplication.attestation === 'true' ||
    schoolApplication.attestation === true,
  accountNumberConfirmation: undefined,
  additionalContacts: [],
  MailingAddressSchema: normalizeMailingAddress(
    schoolApplication,
    providerInfo
  ),

  TuitionSchema: (() => {
    const parsed = TuitionSchema.safeParse({
      chargeTuition:
        schoolApplication?.charges_tuition === 'Yes' ||
        schoolApplication?.charges_tuition === 'No'
          ? schoolApplication.charges_tuition
          : ('No' as 'Yes' | 'No'),
      kindergartenTuition: schoolApplication.kindergarten_tuition,
      elementaryTuition: schoolApplication.elementary_tuition,
      middleSchoolTuition: schoolApplication.middle_school_tuition,
      highSchoolTuition: schoolApplication.high_school_tuition,
      variableTuition: schoolApplication.variable_tuition_forms
        ?.split(',')
        .map((s: string) => s.trim()),
    })

    return parsed.success
      ? parsed.data
      : {
          chargeTuition: (schoolApplication?.charges_tuition === 'Yes' ||
          schoolApplication?.charges_tuition === 'No'
            ? schoolApplication.charges_tuition
            : 'No') as 'Yes' | 'No',
          kindergartenTuition: '',
          elementaryTuition: '',
          middleSchoolTuition: '',
          highSchoolTuition: '',
          variableTuition: [],
        }
  })(),

  StandardizedTestsSchema: (() => {
    const parsed = StandardizedTestsSchema.safeParse({
      isDoingStandardizedTests: schoolApplication.administers_standardized_test,
      standardizedTests:
        schoolApplication.student_assessment_methods
          ?.split(',')
          .map((s: string) => s.trim()) ?? [],
      howAreStudentsAssessed:
        schoolApplication.standardized_tests_administered ?? 'N/A',
    })

    return parsed.success
      ? parsed.data
      : {
          isDoingStandardizedTests:
            schoolApplication.administers_standardized_test === 'Yes' ||
            schoolApplication.administers_standardized_test === 'No'
              ? schoolApplication.administers_standardized_test
              : 'No',
          standardizedTests: [],
          howAreStudentsAssessed: 'N/A',
        }
  })(),

  DemographicsSchema: {
    genderFemale: schoolApplication.gender_female || '',
    genderMale: schoolApplication.gender_male || '',
    genderOther: schoolApplication.gender_other || '',
    ethnicityAfricanAmerican:
      schoolApplication.ethnicity_african_american || '',
    ethnicityAsian: schoolApplication.ethnicity_asian || '',
    ethnicityCaucasian: schoolApplication.ethnicity_caucasian || '',
    ethnicityEastAsian: schoolApplication.ethnicity_east_asian || '',
    ethnicityHispanicLatino: schoolApplication.ethnicity_hispanic_latino || '',
    ethnicityMiddleEastern: schoolApplication.ethnicity_middle_eastern || '',
    ethnicityMultiracial: schoolApplication.ethnicity_multiracial || '',
    ethnicityNativeAmerican: schoolApplication.ethnicity_native_american || '',
    ethnicityNativeHawaiian: schoolApplication.ethnicity_native_hawaiian || '',
    ethnicityPreferNot: schoolApplication.ethnicity_prefer_not_to_say || '',
  },

  BankingSchema: {
    canReceiveACH:
      schoolApplication.receives_ach_payment === 'Yes' ? 'Yes' : 'No',
    federalTaxId: schoolApplication.federal_tax_id || '',
    accountType: schoolApplication.account_type || '',
    depositoryName: schoolApplication.depository_name || '',
    branch: schoolApplication.bank_branch || '',
    bankStreetAddress: schoolApplication.bank_street_address || '',
    bankStreetAddress2: schoolApplication.bank_street_address_line_2 || '',
    bankCity: schoolApplication.bank_city || '',
    bankState: schoolApplication.bank_state || '',
    bankRoutingId: schoolApplication.bank_routing_id || '',
    accountNumber: schoolApplication.account_number || '',
  },
})
